import { bootstrapApplication, HAMMER_GESTURE_CONFIG } from '@angular/platform-browser';
import { AppComponent } from './app/app.component';
import { HTTP_INTERCEPTORS, provideHttpClient, withInterceptorsFromDi } from '@angular/common/http';
import { ToastMessagesService } from './app/commons/services/toast-messages.service';
import { ConfirmationService, MessageService } from 'primeng/api';
import { CurrencyPipe, DatePipe } from '@angular/common'; // Import DatePipe
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { APP_INITIALIZER, ErrorHandler, importProvidersFrom, provideZoneChangeDetection } from '@angular/core';
import { provideRouter, RouteReuseStrategy, Router, TitleStrategy } from '@angular/router';
import { PullRefreshRouteReuseStrategy } from './app/commons/pull-to-refresh/pull-refresh-route-reuse.strategy';
import { AppTitleStrategy } from './app/services/page-title/app-title.strategy';
import { routes } from './app/app-routing.module';
import { TallysheetHttpInterceptorService } from './app/interceptors/tallysheet-http.interceptor';
import { DialogService } from 'primeng/dynamicdialog';
import { FontSizeServiceService } from './app/services/font-size-service.service';
import { LoggerModule, NgxLoggerLevel } from 'ngx-logger';
import { providePrimeNG } from 'primeng/config';
import { LumberLinqPreset } from './app/commons/lumberlinq-preset';
// Guards a PrimeNG InputNumber crash when a non-digit is typed over selected text (see the file).
import './app/commons/patches/primeng-input-number.patch';
// main.ts
import 'hammerjs';
import { MyHammerConfig } from './app/helper/hammer.config';
import { environment } from './environments/environment';
import * as Sentry from '@sentry/angular';

// Silence developer console noise in production.
//
// environment.prod.ts sets logLevel 'ERROR', but that ONLY governs ngx-logger. There are ~974
// raw console.* calls across the app that bypass it completely, and Angular's production build
// does not strip them (no drop_console). That meant real users' consoles received ~855
// console.log calls, several of which dump whole API response bodies -- noise, wasted main-thread
// work, and an information leak.
//
// warn and error are deliberately KEPT: offline-db.ts logs via raw console.error precisely so it
// stays visible on prod regardless of logLevel (its own comment explains a cross-tenant leak that
// took hours to diagnose without it), and Sentry reporting relies on genuine errors surfacing.
// Non-production builds are untouched -- stage/dev keep full logging plus sourcemaps.
if (environment.production) {
  const silence = () => {};
  console.log = silence;
  console.debug = silence;
  console.info = silence;
  console.table = silence;
  console.trace = silence;
}

if (environment.sentryDsn) {
  Sentry.init({
    dsn: environment.sentryDsn,
    environment: environment.production ? 'production' : 'development',
    integrations: [Sentry.browserTracingIntegration()],
    tracesSampleRate: 0.1,
    // 2026-10-05: a script on the page (a browser extension or a third-party tag) that walks every frame trips over the cross-origin
    // video call frame on /crm. It is not our code and nothing breaks, so it is not reported.
    ignoreErrors: [/Blocked a frame with origin .* from accessing a cross-origin frame/],
    // 2026-10-02: a signed-out session (401) is handled by the app itself (refresh, then the login page); it is not a bug to report.
    beforeSend(event, hint) {
      const e: any = hint?.originalException;
      if (e && (e.status === 401 || e.rejection?.status === 401 || e.error?.status === 401)) return null;
      return event;
    },
  });
}
declare const module: any;

function resolveLogLevel(level: string | undefined): NgxLoggerLevel {
  switch ((level || '').toUpperCase()) {
    case 'TRACE':
      return NgxLoggerLevel.TRACE;
    case 'DEBUG':
      return NgxLoggerLevel.DEBUG;
    case 'INFO':
      return NgxLoggerLevel.INFO;
    case 'WARN':
      return NgxLoggerLevel.WARN;
    case 'ERROR':
      return NgxLoggerLevel.ERROR;
    case 'FATAL':
      return NgxLoggerLevel.FATAL;
    case 'OFF':
      return NgxLoggerLevel.OFF;
    default:
      return environment.production ? NgxLoggerLevel.ERROR : NgxLoggerLevel.DEBUG;
  }
}


bootstrapApplication(AppComponent, {
  providers: [
    // Angular v21 defaults to zoneless scheduling even with zone.js still loaded as a polyfill --
    // this app's whole codebase assumes the pre-v21 default of automatic CD after every
    // HTTP/Promise/setTimeout callback, so that default must be restored explicitly here.
    provideZoneChangeDetection({ eventCoalescing: true }),
    provideHttpClient(withInterceptorsFromDi()), // Enable interceptors from DI

    MessageService,
    DatePipe,
    ConfirmationService,
    DialogService,
    FontSizeServiceService,
    CurrencyPipe,
    Location,
    providePrimeNG({
      theme: {
        preset: LumberLinqPreset,
        options: { darkModeSelector: '.app-dark' },
      },
    }),
    importProvidersFrom(
      BrowserAnimationsModule,
      LoggerModule.forRoot({
        level: resolveLogLevel(environment.logLevel),
        serverLogLevel: NgxLoggerLevel.OFF,
      }),
    ),
    provideRouter(routes), // Define your routes here
    // the router's normal reuse rule plus a one-navigation switch used by pull-down refresh (see the file)
    { provide: RouteReuseStrategy, useExisting: PullRefreshRouteReuseStrategy },
    // One place decides every browser-tab title (deepest route title, always set, ends with " | LumberLinq").
    { provide: TitleStrategy, useClass: AppTitleStrategy },
    { provide: HTTP_INTERCEPTORS, useClass: TallysheetHttpInterceptorService, multi: true },
    {
      provide: HAMMER_GESTURE_CONFIG,
      useClass: MyHammerConfig,
    },
    ...(environment.sentryDsn ? [
      { provide: ErrorHandler, useValue: Sentry.createErrorHandler() },
      { provide: Sentry.TraceService, deps: [Router] },
      { provide: APP_INITIALIZER, useFactory: () => () => {}, deps: [Sentry.TraceService], multi: true },
    ] : []),
  ]
})
  .then((appRef) => {
    window['ngRef'] = appRef;
  })
  .catch(err => { throw err; });

// Handle HMR (Optional - can be skipped if --hmr works without it)
if (module['hot']) {
  module['hot'].accept();
  module['hot'].dispose(() => {
    const appRef = window['ngRef'];
    if (appRef) {
      appRef.destroy();
    }
  });
}
